import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyQuery } from '@apollo/client';

import { selectIsAuthenticated } from 'stash/selectors';
import {
	addUserToRedux,
	addProfileToRedux,
	addAccountsToRedux,
	addFundingSourcesRedux,
	addInvestorApplicationToRedux,
	addTrustedContactToRedux,
	addActivePlatformTierToRedux,
	addEligibleAccountTypesToRedux,
	addOnboardingQuestionsToRedux,
	addOnboardingQuestionsAnswersToRedux,
	addCartItemsToRedux,
} from 'stash/actions/apolloGraphql';

import { GET_USER_WITH_ACCOUNTS } from 'stash/graphql/queries/user';
import { GET_ONBOARDING_QUESTIONS_ANSWERS } from 'stash/graphql/queries/onboardingQuestionsAnswers';

import {
	REACTIVATION_LOCAL_STORAGE_KEY,
	REACTIVATION_QUERY_PARAM,
	WEB_APP_PATH,
} from 'stash/constants';
import { useLocation } from 'react-router-dom';
import { RESTRICTED_REASON_CLOSED } from 'stash/constants/mappings';
import { setLocalStorage } from 'stash/utils/storage';

const useGraphQLData = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();
	const isReactivatedParam = new URLSearchParams(search).get(REACTIVATION_QUERY_PARAM);
	if (isReactivatedParam) {
		setLocalStorage(REACTIVATION_LOCAL_STORAGE_KEY, true);
	}
	const isAuthenticated = useSelector(selectIsAuthenticated);

	const [getUserWithAccounts, { error: userResponseErrors, data: userResponse }] =
		useLazyQuery(GET_USER_WITH_ACCOUNTS);
	const [
		getQuestionsAnswers,
		{ error: questionsAnswersResponseErrors, data: questionsAnswersResponse },
	] = useLazyQuery(GET_ONBOARDING_QUESTIONS_ANSWERS);

	useEffect(() => {
		if (isAuthenticated) {
			getUserWithAccounts();
			getQuestionsAnswers();
		}
	}, [isAuthenticated, getUserWithAccounts, getQuestionsAnswers]);

	useEffect(() => {
		const fullUserResponse = userResponse?.userWithAccounts;
		const fullQuestionsAnswersResponse =
			questionsAnswersResponse?.onboardingQuestionsWithAnswers;
		const isReadyToSeedRedux = fullUserResponse && fullQuestionsAnswersResponse;

		if (fullUserResponse && fullUserResponse.has_limited_accounts) {
			if (
				fullUserResponse.user.restricted_reason === RESTRICTED_REASON_CLOSED &&
				!isReactivatedParam
			) {
				return (window.location.href = WEB_APP_PATH + '/archive/documents');
			}
		}
		if (isReadyToSeedRedux) {
			dispatch(addUserToRedux(fullUserResponse.user));
			dispatch(addProfileToRedux(fullUserResponse.profile));
			dispatch(addAccountsToRedux(fullUserResponse.accounts));
			dispatch(addFundingSourcesRedux(fullUserResponse.funding_sources));
			dispatch(addInvestorApplicationToRedux(fullUserResponse.investor_application));
			dispatch(addTrustedContactToRedux(fullUserResponse.trusted_contact));
			dispatch(addActivePlatformTierToRedux(fullUserResponse.active_platform_tier));
			dispatch(addEligibleAccountTypesToRedux(fullUserResponse.eligible_account_types));
			dispatch(addCartItemsToRedux(fullUserResponse.cart_items_with_cards));
			dispatch(
				addOnboardingQuestionsToRedux(fullQuestionsAnswersResponse.onboarding_questions)
			);
			dispatch(
				addOnboardingQuestionsAnswersToRedux(
					fullQuestionsAnswersResponse.onboarding_questions_answers
				)
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userResponse, questionsAnswersResponse, dispatch]);

	return {
		isGraphQLDataReady: !!(
			userResponse?.userWithAccounts &&
			questionsAnswersResponse?.onboardingQuestionsWithAnswers
		),
		userResponse,
		questionsAnswersResponse,
		userResponseErrors,
		questionsAnswersResponseErrors,
	};
};

export default useGraphQLData;
